import React, {useCallback, useEffect, useState} from "react";
import {Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Switch, Tabs, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {Landmark, Plus, RefreshCcw} from "lucide-react";
import {useSelector} from "react-redux";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {RootState} from "../../redux/store";
import {adminRequest, buildQuery, getReturnedObject} from "../../api/adminApi";
import {HeroHeader, Card, SearchField, formatNaira, COLORS} from "../../designSystem";

const {RangePicker} = DatePicker;

const investmentStatusOptions = [
  {label: "All statuses", value: ""},
  {label: "Pending funding", value: 7},
  {label: "Active", value: 2},
  {label: "Matured", value: 4},
  {label: "Withdrawal requested", value: 9},
  {label: "Liquidated", value: 10},
  {label: "Failed", value: 3},
];

const withdrawalStatusOptions = [
  {label: "All statuses", value: ""},
  {label: "Processing", value: 1},
  {label: "Completed", value: 2},
  {label: "Rejected", value: 3},
];

const riskOptions = [
  {label: "Low", value: 1},
  {label: "Medium", value: 2},
  {label: "High", value: 3},
];

const rateTypeOptions = [
  {label: "Fixed", value: 1},
  {label: "Variable", value: 2},
];

const productPresetOptions = [
  {
    label: "Money Market Fund (MMF)",
    value: "MMF",
    formValues: {
      productCode: "MMF",
      name: "Money Market Fund",
      description: "Short-term, lower-risk money market investment product.",
      minimumAmount: 1000,
      tenorDays: 30,
      riskLevel: 1,
      rateType: 2,
      allowsTopUp: true,
      allowsEarlyWithdrawal: true,
    },
  },
  {
    label: "Equity Income",
    value: "EQUITY_INCOME",
    formValues: {
      productCode: "EQUITY_INCOME",
      name: "Equity Income",
      description: "Income-focused equity investment product.",
      minimumAmount: 5000,
      tenorDays: 90,
      riskLevel: 3,
      rateType: 2,
      allowsTopUp: true,
      allowsEarlyWithdrawal: false,
    },
  },
  {
    label: "Fixed Income",
    value: "FIXED_INCOME",
    formValues: {
      productCode: "FIXED_INCOME",
      name: "Fixed Income",
      description: "Fixed-rate investment product for predictable returns.",
      minimumAmount: 10000,
      tenorDays: 180,
      riskLevel: 2,
      rateType: 1,
      allowsTopUp: false,
      allowsEarlyWithdrawal: false,
    },
  },
  {
    label: "Treasury Portfolio",
    value: "TREASURY_PORTFOLIO",
    formValues: {
      productCode: "TREASURY_PORTFOLIO",
      name: "Treasury Portfolio",
      description: "Government-backed treasury investment portfolio.",
      minimumAmount: 10000,
      tenorDays: 365,
      riskLevel: 1,
      rateType: 1,
      allowsTopUp: false,
      allowsEarlyWithdrawal: false,
    },
  },
];

const statusText = (value: any) => {
  const status = Number(value);
  if (status === 2) return "Active";
  if (status === 3) return "Failed";
  if (status === 4) return "Matured";
  if (status === 7) return "Pending funding";
  if (status === 9) return "Withdrawal requested";
  if (status === 10) return "Liquidated";
  return String(value || "-");
};

const statusColor = (value: any) => {
  const status = Number(value);
  if (status === 2) return "green";
  if (status === 3) return "red";
  if (status === 4 || status === 10) return "blue";
  if (status === 9) return "purple";
  return "gold";
};

const withdrawalStatusText = (value: any, row?: any) => row?.statusText || ({
  1: "Pending admin approval",
  2: "Completed",
  3: "Rejected",
} as Record<number, string>)[Number(value)] || String(value || "-");

const withdrawalStatusColor = (value: any) => ({
  1: "gold",
  2: "green",
  3: "red",
} as Record<number, string>)[Number(value)] || "default";

const dateCell = (value: string) => value ? new Date(value).toLocaleString() : "-";
const moneyCell = (value: any) => <span className="font-mono">{formatNaira(value || 0)}</span>;

const productInitialValues = {
  partnerCode: "VFD",
  partnerName: "VFD",
  currency: "NGN",
  minimumAmount: 1000,
  estimatedAnnualYield: 10,
  tenorDays: 30,
  riskLevel: 1,
  rateType: 1,
  allowsTopUp: true,
  allowsEarlyWithdrawal: false,
  isActive: true,
};

function Investments() {
  const {token} = useSelector((state: RootState) => state.account);
  const [investments, setInvestments] = useState<any[]>([]);
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [rateHistory, setRateHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState<any>("");
  const [withdrawalStatus, setWithdrawalStatus] = useState<any>("");
  const [dateRange, setDateRange] = useState<any>(null);
  const [fundingRecord, setFundingRecord] = useState<any>(null);
  const [withdrawalAction, setWithdrawalAction] = useState<"complete" | "reject" | null>(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<any>(null);
  const [withdrawalSubmitting, setWithdrawalSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [rateProduct, setRateProduct] = useState<any>(null);
  const [historyProduct, setHistoryProduct] = useState<any>(null);
  const [investmentDetails, setInvestmentDetails] = useState<any>(null);
  const [form] = Form.useForm();
  const [withdrawalForm] = Form.useForm();
  const [productForm] = Form.useForm();
  const [rateForm] = Form.useForm();

  const loadInvestments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const query = buildQuery({
        searchParam,
        status,
        fromDate: dateRange?.[0]?.startOf("day")?.toISOString(),
        toDate: dateRange?.[1]?.endOf("day")?.toISOString(),
        pageSize: 100,
        pageNumber: 1,
      });
      const payload = await adminRequest(`/investments${query}`, {token});
      const data = getReturnedObject(payload);
      setInvestments(data?.items || []);
    } catch (error: any) {
      message.error(error.message || "Unable to load investments");
    } finally {
      setLoading(false);
    }
  }, [token, searchParam, status, dateRange]);

  const loadWithdrawals = useCallback(async () => {
    if (!token) return;
    setWithdrawalsLoading(true);
    try {
      const query = buildQuery({status: withdrawalStatus});
      const payload = await adminRequest(`/investments/withdrawals${query}`, {token});
      setWithdrawals(getReturnedObject(payload) || []);
    } catch (error: any) {
      message.error(error.message || "Unable to load withdrawal requests");
    } finally {
      setWithdrawalsLoading(false);
    }
  }, [token, withdrawalStatus]);

  const loadProducts = useCallback(async () => {
    if (!token) return;
    setProductsLoading(true);
    try {
      const payload = await adminRequest("/investment-products", {token});
      setProducts(getReturnedObject(payload) || []);
    } catch (error: any) {
      message.error(error.message || "Unable to load investment products");
    } finally {
      setProductsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInvestments();
  }, [loadInvestments]);

  useEffect(() => {
    loadWithdrawals();
  }, [loadWithdrawals]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const confirmFunding = async () => {
    if (!token || !fundingRecord) return;
    const values = await form.validateFields();
    await adminRequest(`/investments/${fundingRecord.investment.id}/funding/confirm`, {
      token,
      method: "POST",
      body: JSON.stringify(values),
    });
    message.success("Investment funding confirmed");
    setFundingRecord(null);
    form.resetFields();
    loadInvestments();
  };

  const submitWithdrawalAction = async () => {
    if (!token || !selectedWithdrawal || !withdrawalAction || withdrawalSubmitting) return;
    const values = await withdrawalForm.validateFields();
    setWithdrawalSubmitting(true);
    try {
      await adminRequest(`/investments/withdrawals/${selectedWithdrawal.id}/${withdrawalAction}`, {
        token,
        method: "POST",
        body: JSON.stringify(values),
      });
      message.success(withdrawalAction === "complete" ? "Withdrawal submitted" : "Withdrawal rejected");
      setWithdrawalAction(null);
      setSelectedWithdrawal(null);
      withdrawalForm.resetFields();
      loadWithdrawals();
      loadInvestments();
    } catch (error: any) {
      Modal.error({
        title: withdrawalAction === "complete" ? "Investment payout failed" : "Unable to reject withdrawal",
        content: error?.message || "The withdrawal action could not be completed.",
      });
    } finally {
      setWithdrawalSubmitting(false);
    }
  };

  const openProductModal = (product?: any) => {
    setSelectedProduct(product || null);
    productForm.setFieldsValue(product || productInitialValues);
    setProductModalOpen(true);
  };

  const applyProductPreset = (productCode: string) => {
    const preset = productPresetOptions.find((option) => option.value === productCode);
    if (!preset) return;

    productForm.setFieldsValue(preset.formValues);
  };

  const saveProduct = async () => {
    if (!token) return;
    const values = await productForm.validateFields();
    await adminRequest(selectedProduct ? `/investment-products/${selectedProduct.id}` : "/investment-products", {
      token,
      method: selectedProduct ? "PUT" : "POST",
      body: JSON.stringify(values),
    });
    message.success(selectedProduct ? "Investment product updated" : "Investment product created");
    setProductModalOpen(false);
    setSelectedProduct(null);
    productForm.resetFields();
    loadProducts();
  };

  const openRateModal = (product: any) => {
    setRateProduct(product);
    rateForm.setFieldsValue({estimatedAnnualYield: product.estimatedAnnualYield});
  };

  const updateRate = async () => {
    if (!token || !rateProduct) return;
    const values = await rateForm.validateFields();
    const payload = {
      ...values,
      effectiveFrom: values.effectiveFrom?.toISOString?.(),
    };
    await adminRequest(`/investment-products/${rateProduct.id}/rate`, {
      token,
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    message.success("Investment rate updated");
    setRateProduct(null);
    rateForm.resetFields();
    loadProducts();
  };

  const viewRateHistory = async (product: any) => {
    if (!token) return;
    setHistoryProduct(product);
    const payload = await adminRequest(`/investment-products/${product.id}/rates`, {token});
    setRateHistory(getReturnedObject(payload) || []);
  };

  const viewInvestmentDetails = async (row: any) => {
    if (!token || !row?.investment?.id) return;
    const payload = await adminRequest(`/investments/${row.investment.id}`, {token});
    setInvestmentDetails(getReturnedObject(payload));
  };

  const totalPrincipal = investments.reduce((sum, row) => sum + Number(row?.investment?.principalAmount || 0), 0);
  const totalBookBalance = investments.reduce((sum, row) => sum + Number(row?.investment?.bookBalance || 0), 0);
  const totalActualBalance = investments.reduce((sum, row) => sum + Number(row?.investment?.actualBalance || 0), 0);
  const totalInterest = investments.reduce((sum, row) => sum + Number(row?.investment?.accruedInterestAmount || 0), 0);
  const pendingFunding = investments.filter((row) => Number(row?.investment?.status) === 7).length;
  const investmentRows = investments.map((row, index) => ({...row, key: row?.investment?.id || index}));
  const withdrawalRows = withdrawals.map((row, index) => ({...row, key: row?.id || index}));
  const productRows = products.map((row, index) => ({...row, key: row?.id || index}));
  const detailRows = investmentDetails?.details?.statement?.map((row: any, index: number) => ({...row, key: index})) || [];

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={Landmark}
          title="Investments"
          subtitle="Manage customer portfolios, VFD funding confirmation, rates, maturity withdrawals and earned interest"
        />

        <div className="grid gap-4 md:grid-cols-4 mt-8">
          <Card className="p-5">
            <div className="text-sm text-slate-500">Principal Held</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.brand}}>{formatNaira(totalPrincipal)}</div>
            <div className="text-sm text-slate-500">{investments.length} portfolios</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Book Balance</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.brand}}>{formatNaira(totalBookBalance)}</div>
            <div className="text-sm text-slate-500">Customer principal remaining</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Actual Balance</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.success}}>{formatNaira(totalActualBalance)}</div>
            <div className="text-sm text-slate-500">Principal plus accrued interest</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Interest Made</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.warning}}>{formatNaira(totalInterest)}</div>
            <div className="text-sm text-slate-500">{pendingFunding} pending funding</div>
          </Card>
        </div>

        <Card className="mt-8 p-5">
          <Tabs
            items={[
              {
                key: "portfolios",
                label: "Portfolios",
                children: (
                  <>
                    <div className="grid gap-3 md:grid-cols-4">
                      <SearchField
                        className="md:col-span-2"
                        placeholder="Search reference, product, email"
                        value={searchParam}
                        onChange={(event) => setSearchParam(event.target.value)}
                      />
                      <Select options={investmentStatusOptions} value={status} onChange={setStatus}/>
                      <RangePicker onChange={setDateRange}/>
                    </div>

                    <GenericTable
                      loading={loading}
                      pageInfo={{pageNumber: 1, pageSize: 100, totalItems: investments.length, totalPages: 1}}
                      data={investmentRows}
                      columns={[
                        {title: "Reference", key: "reference", render: (_: any, row: any) => row.investment?.reference},
                        {title: "User", dataIndex: "userEmail", key: "userEmail"},
                        {title: "Product", key: "product", render: (_: any, row: any) => row.investment?.productName},
                        {title: "Book Balance", key: "bookBalance", render: (_: any, row: any) => moneyCell(row.investment?.bookBalance)},
                        {title: "Actual Balance", key: "actualBalance", render: (_: any, row: any) => moneyCell(row.investment?.actualBalance)},
                        {title: "Interest", key: "interest", render: (_: any, row: any) => moneyCell(row.investment?.accruedInterestAmount)},
                        {title: "Rate", key: "rate", render: (_: any, row: any) => `${row.investment?.estimatedAnnualYield || 0}%`},
                        {title: "Maturity", key: "maturity", render: (_: any, row: any) => dateCell(row.investment?.expectedMaturityDate)},
                        {title: "Status", key: "status", render: (_: any, row: any) => <Tag color={statusColor(row.investment?.status)}>{statusText(row.investment?.status)}</Tag>},
                        {title: "Action", key: "action", render: (_: any, row: any) => (
                          <Space>
                            <Button size="small" onClick={() => viewInvestmentDetails(row)}>Details</Button>
                            {Number(row.investment?.status) === 7 ? (
                              <Button size="small" type="primary" onClick={() => setFundingRecord(row)}>
                                Confirm funding
                              </Button>
                            ) : null}
                          </Space>
                        )},
                      ]}
                    />
                  </>
                ),
              },
              {
                key: "withdrawals",
                label: "Withdrawals",
                children: (
                  <>
                    <div className="mb-3 w-full md:w-64">
                      <Select className="w-full" options={withdrawalStatusOptions} value={withdrawalStatus} onChange={setWithdrawalStatus}/>
                    </div>
                    <GenericTable
                      loading={withdrawalsLoading}
                      pageInfo={{pageNumber: 1, pageSize: 100, totalItems: withdrawals.length, totalPages: 1}}
                      data={withdrawalRows}
                      columns={[
                        {title: "Reference", dataIndex: "transactionReference", key: "transactionReference"},
                        {title: "Investment", dataIndex: "investmentReference", key: "investmentReference"},
                        {title: "User", dataIndex: "userEmail", key: "userEmail"},
                        {title: "Amount", dataIndex: "amount", key: "amount", render: moneyCell},
                        {title: "Bank", key: "bank", render: (_: any, row: any) => `${row.bankName || "-"} · ${row.accountNumber || "-"}`},
                        {title: "Status", dataIndex: "status", key: "status", render: (value: any, row: any) => <Tag color={withdrawalStatusColor(value)}>{withdrawalStatusText(value, row)}</Tag>},
                        {title: "Created", dataIndex: "dateCreated", key: "dateCreated", render: dateCell},
                        {title: "Action", key: "action", render: (_: any, row: any) => Number(row.status) === 1 ? (
                          <Space>
                            <Button size="small" type="primary" disabled={withdrawalSubmitting} onClick={() => { setSelectedWithdrawal(row); setWithdrawalAction("complete"); }}>
                              Complete payout
                            </Button>
                            <Button size="small" danger disabled={withdrawalSubmitting} onClick={() => { setSelectedWithdrawal(row); setWithdrawalAction("reject"); }}>
                              Reject
                            </Button>
                          </Space>
                        ) : "-"},
                      ]}
                    />
                  </>
                ),
              },
              {
                key: "products",
                label: "Products & Rates",
                children: (
                  <>
                    <div className="mb-3 flex justify-end">
                      <Button type="primary" icon={<Plus size={16}/>} onClick={() => openProductModal()}>
                        New Product
                      </Button>
                    </div>
                    <GenericTable
                      loading={productsLoading}
                      pageInfo={{pageNumber: 1, pageSize: 100, totalItems: products.length, totalPages: 1}}
                      data={productRows}
                      columns={[
                        {title: "Product", dataIndex: "name", key: "name"},
                        {title: "Partner", dataIndex: "partnerName", key: "partnerName"},
                        {title: "Code", dataIndex: "productCode", key: "productCode"},
                        {title: "Minimum", dataIndex: "minimumAmount", key: "minimumAmount", render: moneyCell},
                        {title: "Rate", key: "rate", render: (_: any, row: any) => `${row.estimatedAnnualYield || 0}%`},
                        {title: "Tenor", key: "tenor", render: (_: any, row: any) => `${row.tenorDays || 0} days`},
                        {title: "Type", key: "type", render: (_: any, row: any) => Number(row.rateType) === 2 ? "Variable" : "Fixed"},
                        {title: "Status", key: "status", render: (_: any, row: any) => <Tag color={row.isActive ? "green" : "red"}>{row.isActive ? "Active" : "Inactive"}</Tag>},
                        {title: "Action", key: "action", render: (_: any, row: any) => (
                          <Space>
                            <Button size="small" onClick={() => openProductModal(row)}>Edit</Button>
                            <Button size="small" icon={<RefreshCcw size={14}/>} onClick={() => openRateModal(row)}>Rate</Button>
                            <Button size="small" onClick={() => viewRateHistory(row)}>History</Button>
                          </Space>
                        )},
                      ]}
                    />
                  </>
                ),
              },
            ]}
          />
        </Card>

        <Modal title="Confirm VFD Funding" open={!!fundingRecord} onOk={confirmFunding} onCancel={() => setFundingRecord(null)} okText="Confirm funding">
          <Form form={form} layout="vertical">
            <Form.Item label="VFD / Partner Reference" name="partnerReference">
              <Input placeholder="Reference from VFD investment account"/>
            </Form.Item>
            <Form.Item label="Funding Note" name="fundingNote">
              <Input.TextArea rows={3} placeholder="Optional operation note"/>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={withdrawalAction === "complete" ? "Complete Investment Payout" : "Reject Investment Withdrawal"}
          open={!!withdrawalAction}
          onOk={submitWithdrawalAction}
          onCancel={() => {
            if (!withdrawalSubmitting) {
              setWithdrawalAction(null);
              setSelectedWithdrawal(null);
            }
          }}
          okText={withdrawalSubmitting ? "Submitting..." : withdrawalAction === "complete" ? "Complete payout" : "Reject"}
          confirmLoading={withdrawalSubmitting}
          okButtonProps={{danger: withdrawalAction === "reject", disabled: withdrawalSubmitting}}
          cancelButtonProps={{disabled: withdrawalSubmitting}}
        >
          <Form form={withdrawalForm} layout="vertical">
            {withdrawalAction === "complete" ? (
              <>
                <Form.Item label="External Reference" name="externalReference">
                  <Input placeholder="Bank/VFD transfer reference"/>
                </Form.Item>
                <Form.Item label="Payment Proof URL" name="paymentProofUrl">
                  <Input placeholder="Optional proof URL"/>
                </Form.Item>
                <Form.Item label="Narration" name="narration">
                  <Input.TextArea rows={3}/>
                </Form.Item>
              </>
            ) : (
              <Form.Item label="Reason" name="reason" rules={[{required: true, message: "Rejection reason is required"}]}>
                <Input.TextArea rows={3}/>
              </Form.Item>
            )}
          </Form>
        </Modal>

        <Modal title={selectedProduct ? "Edit Investment Product" : "Create Investment Product"} open={productModalOpen} onOk={saveProduct} onCancel={() => setProductModalOpen(false)} width={720}>
          <Form form={productForm} layout="vertical" initialValues={productInitialValues}>
            <div className="grid gap-3 md:grid-cols-2">
              <Form.Item label="Partner Code" name="partnerCode" rules={[{required: true}]}>
                <Input/>
              </Form.Item>
              <Form.Item label="Partner Name" name="partnerName" rules={[{required: true}]}>
                <Input/>
              </Form.Item>
              <Form.Item label="Product Template" name="productTemplate">
                <Select
                  allowClear
                  showSearch
                  placeholder="Select a product template"
                  options={productPresetOptions.map(({label, value}) => ({label, value}))}
                  onChange={applyProductPreset}
                />
              </Form.Item>
              <Form.Item label="Product Code" name="productCode" rules={[{required: true}]}>
                <Select
                  showSearch
                  placeholder="Select or type product code"
                  options={productPresetOptions.map(({label, value}) => ({label: `${label} - ${value}`, value}))}
                  onChange={applyProductPreset}
                />
              </Form.Item>
              <Form.Item label="Product Name" name="name" rules={[{required: true}]}>
                <Input/>
              </Form.Item>
              <Form.Item label="Minimum Amount" name="minimumAmount" rules={[{required: true}]}>
                <InputNumber className="w-full" min={1}/>
              </Form.Item>
              <Form.Item label="Annual Yield (%)" name="estimatedAnnualYield" rules={[{required: true}]}>
                <InputNumber className="w-full" min={0.01} step={0.01}/>
              </Form.Item>
              <Form.Item label="Tenor Days" name="tenorDays" rules={[{required: true}]}>
                <InputNumber className="w-full" min={1}/>
              </Form.Item>
              <Form.Item label="Currency" name="currency" rules={[{required: true}]}>
                <Input/>
              </Form.Item>
              <Form.Item label="Risk Level" name="riskLevel">
                <Select options={riskOptions}/>
              </Form.Item>
              <Form.Item label="Rate Type" name="rateType">
                <Select options={rateTypeOptions}/>
              </Form.Item>
            </div>
            <Form.Item label="Description" name="description">
              <Input.TextArea rows={3}/>
            </Form.Item>
            <div className="grid gap-3 md:grid-cols-3">
              <Form.Item label="Allows Top Up" name="allowsTopUp" valuePropName="checked">
                <Switch/>
              </Form.Item>
              <Form.Item label="Allows Early Withdrawal" name="allowsEarlyWithdrawal" valuePropName="checked">
                <Switch/>
              </Form.Item>
              <Form.Item label="Active" name="isActive" valuePropName="checked">
                <Switch/>
              </Form.Item>
            </div>
          </Form>
        </Modal>

        <Modal title={`Update Rate${rateProduct ? `: ${rateProduct.name}` : ""}`} open={!!rateProduct} onOk={updateRate} onCancel={() => setRateProduct(null)}>
          <Form form={rateForm} layout="vertical">
            <Form.Item label="Annual Yield (%)" name="estimatedAnnualYield" rules={[{required: true}]}>
              <InputNumber className="w-full" min={0.01} step={0.01}/>
            </Form.Item>
            <Form.Item label="Effective From" name="effectiveFrom">
              <DatePicker className="w-full"/>
            </Form.Item>
            <Form.Item label="Note" name="note">
              <Input.TextArea rows={3}/>
            </Form.Item>
          </Form>
        </Modal>

        <Modal title={`Rate History${historyProduct ? `: ${historyProduct.name}` : ""}`} open={!!historyProduct} onCancel={() => setHistoryProduct(null)} footer={null} width={760}>
          <GenericTable
            pageInfo={{pageNumber: 1, pageSize: 100, totalItems: rateHistory.length, totalPages: 1}}
            data={rateHistory.map((row, index) => ({...row, key: row.id || index}))}
            columns={[
              {title: "Old Rate", dataIndex: "oldAnnualYield", key: "oldAnnualYield", render: (value: any) => `${value || 0}%`},
              {title: "New Rate", dataIndex: "newAnnualYield", key: "newAnnualYield", render: (value: any) => `${value || 0}%`},
              {title: "Effective", dataIndex: "effectiveFrom", key: "effectiveFrom", render: dateCell},
              {title: "Updated By", dataIndex: "updatedByAdminEmail", key: "updatedByAdminEmail"},
              {title: "Note", dataIndex: "note", key: "note"},
            ]}
          />
        </Modal>

        <Modal title="Investment Details" open={!!investmentDetails} onCancel={() => setInvestmentDetails(null)} footer={null} width={920}>
          {investmentDetails?.details ? (
            <>
              <div className="grid gap-3 md:grid-cols-3">
                <Card className="p-4">
                  <div className="text-sm text-slate-500">Book Balance</div>
                  <div className="text-xl font-semibold">{formatNaira(investmentDetails.details.bookBalance)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-slate-500">Actual Balance</div>
                  <div className="text-xl font-semibold">{formatNaira(investmentDetails.details.actualBalance)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-slate-500">Interest Made</div>
                  <div className="text-xl font-semibold">{formatNaira(investmentDetails.details.accruedInterestAmount)}</div>
                </Card>
              </div>
              <div className="mt-4 text-sm text-slate-600">
                {investmentDetails.userName || investmentDetails.userEmail} · {investmentDetails.details.reference}
              </div>
              <GenericTable
                pageInfo={{pageNumber: 1, pageSize: 100, totalItems: detailRows.length, totalPages: 1}}
                data={detailRows}
                columns={[
                  {title: "Date", dataIndex: "date", key: "date", render: dateCell},
                  {title: "Type", dataIndex: "type", key: "type"},
                  {title: "Debit", dataIndex: "debit", key: "debit", render: moneyCell},
                  {title: "Credit", dataIndex: "credit", key: "credit", render: moneyCell},
                  {title: "Balance", dataIndex: "balance", key: "balance", render: moneyCell},
                  {title: "Narration", dataIndex: "narration", key: "narration"},
                  {title: "Status", dataIndex: "status", key: "status"},
                ]}
              />
            </>
          ) : null}
        </Modal>
      </main>
    </AdminLayout>
  );
}

export default Investments;
